/**
 * PartellTab.tsx
 *
 * The patient's Partell (RequestMyMeds) prescriptions: the list, and the button
 * that writes a new one.
 *
 * Deliberately its own section rather than part of Prescriptions. That tab is
 * our internal medication record; this one is a pharmacy queue — every row here
 * was submitted to Partell and carries their status and tracking. Mixing the
 * two would put records nobody sent next to records somebody is waiting on.
 *
 * The table is the shared IntegrationOrdersSubTab, the same one Peptides uses:
 * an RMM order is an integration_order like any other, so status, tracking and
 * the cancelled toggle come for free.
 */
import { useQueryClient } from '@tanstack/react-query';
import { FilePlus2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { IntegrationOrdersSubTab } from './labs/IntegrationOrdersSubTab';
import { useAxiomOrderFlow } from './labs/useAxiomOrderFlow';
import { linkedOrderIdFor, type LinkedOrder } from './labs/linkedOrder';

/**
 * Partell's own status vocabulary, spelled the way the pharmacy says it. Left
 * unmapped to our fulfilment words on purpose: staff phoning the pharmacy
 * should be reading the same status the pharmacy is.
 */
export const RMM_FULFILLMENT_LABELS: Record<string, string> = {
  // Ours, before Partell has it.
  awaiting_payment: 'Awaiting payment',
  scheduled: 'Scheduled',
  submitting: 'Sending',
  error: 'Error',
  cancelled: 'Cancelled',
  // Partell's.
  received: 'Received',
  processed: 'Processed',
  shipped: 'Shipped',
  completed: 'Completed',
  rejected: 'Rejected',
};

export const RMM_STATUS_HELP: Record<string, string> = {
  received: 'Partell has the prescription; it has not been reviewed yet.',
  processed: 'Reviewed and queued for the pharmacy to fill.',
  shipped: 'Out for delivery — a tracking number is available.',
  completed: 'Filled and delivered, or picked up at the pharmacy.',
  rejected: 'Partell declined it. Call the pharmacy before resubmitting.',
};

export function PartellTab({ patientId, linkedOrder, onLinkedOrderOpened }: {
  patientId: number;
  /** A link straight to one order (linkedOrder.ts): opens its details on arrival. */
  linkedOrder?: LinkedOrder | null;
  onLinkedOrderOpened?: () => void;
}) {
  const queryClient = useQueryClient();

  /**
   * The shared order flow — the same one Peptides uses, keyed to 'rmm'. It owns
   * the prescription form (new, re-order, clone, re-send, edit), taking the
   * payment, cancelling with a confirmation, retrying, and the modals for all
   * of it, so a Partell order offers exactly the actions a peptide order does.
   */
  const flow = useAxiomOrderFlow({
    integrationKey: 'rmm',
    onRefresh: () => void queryClient.invalidateQueries({ queryKey: ['integration-orders', patientId] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Partell Pharmacy</h2>
          <p className="text-[11px] text-slate-500">
            Prescriptions submitted to Partell, and where each one is.
          </p>
        </div>
        <Button size="sm" onClick={() => flow.startNewOrder(patientId)}>
          <FilePlus2 className="mr-1.5 h-4 w-4" />
          New prescription
        </Button>
      </div>

      <IntegrationOrdersSubTab
        patientId={patientId}
        integrationKey="rmm"
        fulfillmentLabels={RMM_FULFILLMENT_LABELS}
        statusHelp={RMM_STATUS_HELP}
        hideCancelled
        emptyMessage="No prescriptions sent to Partell yet."
        onFinishPayment={(o) => flow.finishPayment(o, patientId)}
        onCancel={(o) => void flow.cancel(o, patientId)}
        onRetry={(o) => void flow.retry(o, patientId)}
        onEdit={(o) => flow.edit(o, patientId)}
        onClone={(o) => flow.clone(o, patientId)}
        onResend={(o) => flow.resend(o, patientId)}
        onReorder={(o) => flow.reorder(o, patientId)}
        openOrderId={linkedOrderIdFor(linkedOrder, 'rmm')}
        onOrderOpened={onLinkedOrderOpened}
      />

      {flow.modals}
    </div>
  );
}
